import * as THREE from 'three';
import { gsap } from 'gsap';
import { lenis } from './Scroll';

const TWO_PI = Math.PI * 2;

const CONFIG = {
  selectors: {
    canvas: '#home-ring',
    images: '.p-home__intro-img img',
  },
  camera: {
    fov: 38,
    near: 0.1,
    far: 100,
    padding: 0.92,
    desktop: { positionY: 0, depthOffset: 0 },
    mobile: { positionY: 0.5, depthOffset: -2.5 },
  },
  ring: {
    radius: 3,
    fallbackImageRatio: 2034 / 1260,
    imageHeightScale: 1.395,
    textureEdgeInset: 0.003,
    gap: 0.05,
    curveSegments: 16,
    initialRotation: 8,
  },
  layout: {
    desktopAspect: 1.4,
    desktop: { tiltX: 12, tiltZ: 30, positionX: 0.72 },
    mobile: { tiltX: 20, tiltZ: 26, positionX: 0 },
  },
  motion: {
    idleDuration: 80,
    scrollRotation: 0.0008,
    acceleration: 8,
    inertia: 2.4,
    maxScrollVelocity: 100,
  },
  renderer: {
    maxPixelRatio: 2,
    maxRenderPixels: 3500000,
    maxTextureWidth: 1600,
    maxAnisotropy: 8,
    saturation: 1.08,
    contrast: 1.04,
  },
};

const instances = new WeakMap();

function createTileGeometry(imageCount, imageRatio) {
  const { radius, imageHeightScale, textureEdgeInset, gap, curveSegments } = CONFIG.ring;
  const angleStep = TWO_PI / imageCount;
  const tileAngle = angleStep * (1 - gap);
  const width = radius * tileAngle;
  const height = (width / imageRatio) * imageHeightScale;
  const geometry = new THREE.PlaneGeometry(width, height, curveSegments, 1);
  const positions = geometry.attributes.position;
  const uvs = geometry.attributes.uv;

  for (let index = 0; index < positions.count; index += 1) {
    const angle = positions.getX(index) / radius;
    positions.setX(index, Math.sin(angle) * radius);
    positions.setZ(index, Math.cos(angle) * radius);
  }

  // object-fit: coverと同様に、画像比率を保ったまま中央をクロップする
  const tileRatio = width / height;
  if (imageRatio > tileRatio) {
    const visibleWidth = tileRatio / imageRatio;
    for (let index = 0; index < uvs.count; index += 1) {
      uvs.setX(index, 0.5 + (uvs.getX(index) - 0.5) * visibleWidth);
    }
  } else if (imageRatio < tileRatio) {
    const visibleHeight = imageRatio / tileRatio;
    for (let index = 0; index < uvs.count; index += 1) {
      uvs.setY(index, 0.5 + (uvs.getY(index) - 0.5) * visibleHeight);
    }
  }

  // 元画像の最上端・最下端を避け、縮小時に生じる1px前後の縁を表示範囲から外す
  for (let index = 0; index < uvs.count; index += 1) {
    uvs.setY(index, textureEdgeInset + uvs.getY(index) * (1 - textureEdgeInset * 2));
  }

  positions.needsUpdate = true;
  uvs.needsUpdate = true;
  geometry.computeVertexNormals();

  return { geometry, angleStep };
}

async function createTexture(image, renderer, viewportWidth) {
  const { maxPixelRatio, maxTextureWidth, maxAnisotropy } = CONFIG.renderer;
  const pixelRatio = Math.min(window.devicePixelRatio || 1, maxPixelRatio);
  const imageRatio = image.naturalWidth / image.naturalHeight || CONFIG.ring.fallbackImageRatio;
  const width = Math.min(Math.max(512, Math.round(viewportWidth * pixelRatio)), maxTextureWidth, image.naturalWidth || maxTextureWidth);
  const height = Math.round(width / imageRatio);

  const bitmapOptions = {
    imageOrientation: 'flipY',
    resizeWidth: width,
    resizeHeight: height,
    resizeQuality: 'high',
  };

  let texture;
  if ('createImageBitmap' in window) {
    const src = image.currentSrc || image.src;
    try {
      const response = await fetch(src);
      if (response.ok) {
        const blob = await response.blob();
        const bitmap = await window.createImageBitmap(blob, bitmapOptions);
        texture = new THREE.Texture(bitmap);
      } else {
        throw new Error('fetch failed');
      }
    } catch {
      if (image.decode) await image.decode();
      try {
        const bitmap = await window.createImageBitmap(image, bitmapOptions);
        texture = new THREE.Texture(bitmap);
      } catch {
        texture = new THREE.Texture(image);
      }
    }
  } else {
    if (image.decode) await image.decode();
    texture = new THREE.Texture(image);
  }

  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = true;
  texture.anisotropy = Math.min(maxAnisotropy, renderer.capabilities.getMaxAnisotropy());
  texture.needsUpdate = true;

  return texture;
}

function disposeTexture(texture) {
  texture.dispose();
  texture.image?.close?.();
}

function createRenderer(canvas) {
  try {
    const renderer = new THREE.WebGLRenderer({
      canvas,
      alpha: true,
      premultipliedAlpha: false,
      // 透明キャンバスのMSAA解決時に、画像端へクリアカラーの黒が混ざるのを防ぐ
      antialias: false,
      powerPreference: 'high-performance',
    });

    renderer.setClearColor(0x000000, 0);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    return renderer;
  } catch (error) {
    console.warn('[ImageRing] WebGL renderer could not be created.', error);
    return null;
  }
}

function createImageMaterial(texture) {
  const colorUniforms = {
    saturation: { value: CONFIG.renderer.saturation },
    contrast: { value: CONFIG.renderer.contrast },
  };
  const material = new THREE.MeshBasicMaterial({
    map: texture,
    side: THREE.DoubleSide,
    toneMapped: false,
  });

  material.onBeforeCompile = (shader) => {
    shader.uniforms.imageSaturation = colorUniforms.saturation;
    shader.uniforms.imageContrast = colorUniforms.contrast;
    shader.fragmentShader = `
      uniform float imageSaturation;
      uniform float imageContrast;
      ${shader.fragmentShader}
    `.replace(
      '#include <map_fragment>',
      `#include <map_fragment>
      float imageLuma = dot(diffuseColor.rgb, vec3(0.2126, 0.7152, 0.0722));
      diffuseColor.rgb = mix(vec3(imageLuma), diffuseColor.rgb, imageSaturation);
      diffuseColor.rgb = clamp((diffuseColor.rgb - 0.5) * imageContrast + 0.5, 0.0, 1.0);`
    );
  };
  material.customProgramCacheKey = () => 'image-ring-color-v1';
  material.userData.imageRingColor = colorUniforms;

  return material;
}

function ImageRing() {
  const canvas = document.querySelector(CONFIG.selectors.canvas);
  if (!(canvas instanceof HTMLCanvasElement)) return null;

  const currentInstance = instances.get(canvas);
  if (currentInstance) return currentInstance;

  const images = [...document.querySelectorAll(CONFIG.selectors.images)].filter((image) => image.src);
  if (!images.length) return null;

  const renderer = createRenderer(canvas);
  if (!renderer) return null;

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(CONFIG.camera.fov, 1, CONFIG.camera.near, CONFIG.camera.far);
  const world = new THREE.Group();
  const ring = new THREE.Group();
  const imageRatio = images[0].naturalWidth / images[0].naturalHeight || CONFIG.ring.fallbackImageRatio;
  const angleStep = TWO_PI / images.length;
  const { geometry } = createTileGeometry(images.length, imageRatio);

  ring.rotation.y = THREE.MathUtils.degToRad(CONFIG.ring.initialRotation);
  world.add(ring);
  scene.add(world);

  const textures = [];
  const materials = [];
  const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const idleAngularVelocity = TWO_PI / CONFIG.motion.idleDuration;

  let viewportWidth = document.documentElement.clientWidth;
  let resizeFrame = 0;
  let scrollVelocity = 0;
  let scrollAngularVelocity = 0;
  let isInView = true;
  let isDestroyed = false;

  const resize = () => {
    resizeFrame = 0;
    if (isDestroyed) return;

    const width = Math.max(1, canvas.clientWidth);
    const height = Math.max(1, canvas.clientHeight);
    const aspect = width / height;
    const limitingAspect = Math.min(aspect, 1);
    const layout = aspect > CONFIG.layout.desktopAspect ? CONFIG.layout.desktop : CONFIG.layout.mobile;
    const cameraPosition = aspect > CONFIG.layout.desktopAspect ? CONFIG.camera.desktop : CONFIG.camera.mobile;

    camera.aspect = aspect;
    camera.position.y = cameraPosition.positionY;
    camera.position.z = (CONFIG.ring.radius * CONFIG.camera.padding) / (Math.tan(THREE.MathUtils.degToRad(CONFIG.camera.fov * 0.5)) * limitingAspect) + cameraPosition.depthOffset;
    camera.updateProjectionMatrix();

    world.position.x = CONFIG.ring.radius * layout.positionX;
    world.rotation.set(THREE.MathUtils.degToRad(layout.tiltX), 0, THREE.MathUtils.degToRad(layout.tiltZ));

    const pixelBudgetRatio = Math.sqrt(CONFIG.renderer.maxRenderPixels / (width * height));
    const pixelRatio = Math.max(1, Math.min(window.devicePixelRatio || 1, CONFIG.renderer.maxPixelRatio, pixelBudgetRatio));

    renderer.setPixelRatio(pixelRatio);
    renderer.setSize(width, height, false);
  };

  const requestResize = () => {
    // SPのツールバー開閉など、高さだけのresizeでは再計算しない
    const nextWidth = document.documentElement.clientWidth;
    if (nextWidth === viewportWidth || resizeFrame) return;

    viewportWidth = nextWidth;
    resizeFrame = window.requestAnimationFrame(resize);
  };

  const updateScroll = ({ velocity }) => {
    scrollVelocity = velocity;
  };

  const suspendMotion = () => {
    if (isInView && !document.hidden) return;
    scrollVelocity = 0;
    scrollAngularVelocity = 0;
  };

  const render = (_time, deltaTime) => {
    if (isDestroyed || !isInView || document.hidden) return;

    if (!reduceMotionQuery.matches) {
      const deltaSeconds = THREE.MathUtils.clamp(deltaTime / 1000, 1 / 240, 0.05);
      const absoluteScrollVelocity = Math.min(Math.abs(scrollVelocity), CONFIG.motion.maxScrollVelocity);
      const targetAngularVelocity = (absoluteScrollVelocity * CONFIG.motion.scrollRotation) / deltaSeconds;
      const damping = targetAngularVelocity > scrollAngularVelocity ? CONFIG.motion.acceleration : CONFIG.motion.inertia;

      scrollAngularVelocity = THREE.MathUtils.damp(scrollAngularVelocity, targetAngularVelocity, damping, deltaSeconds);
      ring.rotation.y = (ring.rotation.y + (idleAngularVelocity + scrollAngularVelocity) * deltaSeconds) % TWO_PI;
    }

    renderer.render(scene, camera);
  };

  const observer =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          ([entry]) => {
            isInView = entry.isIntersecting;
            suspendMotion();
          },
          { rootMargin: '100px 0px' }
        )
      : null;

  const destroy = () => {
    if (isDestroyed) return;
    isDestroyed = true;

    window.removeEventListener('resize', requestResize);
    document.removeEventListener('visibilitychange', suspendMotion);
    if (resizeFrame) window.cancelAnimationFrame(resizeFrame);
    observer?.disconnect();
    lenis.off('scroll', updateScroll);
    gsap.ticker.remove(render);

    geometry.dispose();
    materials.forEach((material) => material.dispose());
    textures.forEach(disposeTexture);
    scene.clear();
    renderer.dispose();
    renderer.forceContextLoss();
    instances.delete(canvas);
  };

  const instance = {
    scene,
    camera,
    renderer,
    world,
    ring,
    resize,
    destroy,
  };

  instances.set(canvas, instance);
  resize();
  observer?.observe(canvas);
  lenis.on('scroll', updateScroll);
  gsap.ticker.add(render);
  window.addEventListener('resize', requestResize, { passive: true });
  document.addEventListener('visibilitychange', suspendMotion);

  Promise.all(
    images.map((image) =>
      createTexture(image, renderer, viewportWidth).catch((error) => {
        console.warn(`[ImageRing] Texture could not be loaded: ${image.currentSrc || image.src}`, error);
        return null;
      })
    )
  ).then((loadedTextures) => {
    if (isDestroyed) {
      loadedTextures.filter(Boolean).forEach(disposeTexture);
      return;
    }

    loadedTextures.forEach((texture, index) => {
      if (!texture) return;

      const material = createImageMaterial(texture);
      const mesh = new THREE.Mesh(geometry, material);

      mesh.rotation.y = index * angleStep;
      ring.add(mesh);
      textures.push(texture);
      materials.push(material);
    });
  });

  return instance;
}

export { ImageRing };
